import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';

const STATUS_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'OPEN', label: '待處理' },
  { value: 'IN_REVIEW', label: '審核中' },
  { value: 'RESOLVED', label: '已結案' },
  { value: 'REJECTED', label: '已駁回' },
];

const REASON_LABEL = {
  SPAM: '垃圾訊息',
  SCAM: '詐騙',
  HARASSMENT: '騷擾',
  SEXUAL_CONTENT: '色情',
  VIOLENCE: '暴力',
  IMPERSONATION: '冒充',
  ILLEGAL_CONTENT: '違法',
  OTHER: '其他',
};

const MUTE_DAY_OPTIONS = [
  { value: 1, label: '1 天' },
  { value: 3, label: '3 天' },
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
  { value: 0, label: '永久' },
];

function formatMember(id, username, displayName) {
  if (username) return `${username}（${id}）`;
  if (displayName) return `${displayName}（${id}）`;
  return id ?? '—';
}

export default function ImReportPage() {
  const [status, setStatus] = useState('');
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [edit, setEdit] = useState(null);

  async function load(pageNo = 1, nextStatus = status) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.imReportPageQuery({
        pageNo,
        pageSize: 20,
        status: nextStatus || undefined,
      });
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveEdit() {
    if (!edit?.id) return;
    setError('');
    try {
      const payload = {
        status: edit.status,
        adminNote: edit.adminNote || '',
        actionType: edit.status === 'RESOLVED' ? edit.actionType : 'NONE',
      };
      if (payload.actionType === 'MUTE') {
        payload.muteDays = Number(edit.muteDays) > 0 ? Number(edit.muteDays) : 0;
      }
      await adminApi.updateImReport(edit.id, payload);
      setMessage('已更新');
      setEdit(null);
      load(page);
    } catch (err) {
      setError(err.message || '更新失敗');
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>IM 檢舉案件</h2>
      </div>

      <form
        className="filter-bar"
        onSubmit={(e) => {
          e.preventDefault();
          setMessage('');
          load(1, status);
        }}
      >
        <label>
          狀態
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn btn-primary">
          查詢
        </button>
      </form>

      {error ? <p className="form-error">{error}</p> : null}
      {message ? <p className="form-success">{message}</p> : null}
      {loading ? <p>載入中…</p> : null}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>檢舉人</th>
              <th>被檢舉</th>
              <th>原因</th>
              <th>狀態</th>
              <th>處置</th>
              <th>時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{formatMember(row.reporterMemberId, row.reporterUsername, row.reporterDisplayName)}</td>
                <td>{formatMember(row.reportedMemberId, row.reportedUsername, row.reportedDisplayName)}</td>
                <td>{REASON_LABEL[row.reason] || row.reason}</td>
                <td>{row.status}</td>
                <td>
                  {row.actionType || '—'}
                  {row.actionType === 'MUTE'
                    ? row.muteDays
                      ? ` ${row.muteDays}天`
                      : ' 永久'
                    : ''}
                </td>
                <td>{row.createTime || '—'}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() =>
                      setEdit({
                        id: row.id,
                        status: row.status === 'OPEN' ? 'RESOLVED' : row.status || 'RESOLVED',
                        actionType: 'MUTE',
                        muteDays: 3,
                        adminNote: row.adminNote || '',
                        description: row.description || '',
                        reportedLabel: formatMember(
                          row.reportedMemberId,
                          row.reportedUsername,
                          row.reportedDisplayName
                        ),
                      })
                    }
                  >
                    處理
                  </button>
                </td>
              </tr>
            ))}
            {!loading && !records.length ? (
              <tr>
                <td colSpan={8}>無資料</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => load(p)} />
      {!loading ? <p className="muted">共 {total} 筆</p> : null}

      {edit ? (
        <div className="confirm-dialog-overlay" role="presentation" onClick={() => setEdit(null)}>
          <div
            className="modal-panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-panel__header">
              <h3>處理檢舉 #{edit.id}</h3>
            </div>
            <p>被檢舉人：{edit.reportedLabel}</p>
            {edit.description ? <p>說明：{edit.description}</p> : null}
            <label>
              案件狀態
              <select
                value={edit.status}
                onChange={(e) => setEdit((prev) => ({ ...prev, status: e.target.value }))}
              >
                {STATUS_OPTIONS.filter((o) => o.value).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            {edit.status === 'RESOLVED' ? (
              <>
                <label>
                  對會員處置
                  <select
                    value={edit.actionType}
                    onChange={(e) => setEdit((prev) => ({ ...prev, actionType: e.target.value }))}
                  >
                    <option value="NONE">僅結案（不處置）</option>
                    <option value="MUTE">禁言（不可聊天，可看歷史）</option>
                    <option value="DISABLE">禁用（完全不能使用）</option>
                  </select>
                </label>
                {edit.actionType === 'MUTE' ? (
                  <label>
                    禁言天數
                    <select
                      value={edit.muteDays}
                      onChange={(e) =>
                        setEdit((prev) => ({ ...prev, muteDays: Number(e.target.value) }))
                      }
                    >
                      {MUTE_DAY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </>
            ) : null}
            <label>
              備註
              <textarea
                rows={3}
                value={edit.adminNote}
                onChange={(e) => setEdit((prev) => ({ ...prev, adminNote: e.target.value }))}
              />
            </label>
            <div className="modal-panel__footer">
              <button type="button" className="btn btn-secondary" onClick={() => setEdit(null)}>
                取消
              </button>
              <button type="button" className="btn btn-primary" onClick={saveEdit}>
                儲存
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
